// eslint-disable @change/sibling-module-imports

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type {
	FacebookPost,
	FullPostType,
	RedditPost,
	SharingModalProps,
	SharingProps,
} from 'src/app/pages/starterDashboardV2/shared/components';
import type { RecommendedGroup, ShareVisual } from 'src/app/pages/starterDashboardV2/shared/types';
import type { ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';

type MessageBody = {
	body?: string;
};

type ImageStrategy = 'shareVisual' | 'readyToSharePost' | 'petitionFallback';

export type PostConfigRow = Readonly<{
	platform: SharingModalProps['platform'];
	postType: SharingModalProps['postType'];
	requiresRecommendedGroup: boolean;
	requiresShareVisual: boolean;
	imageStrategy: ImageStrategy;
	includeMessage: boolean;
	requiresGroupSelection?: boolean;
	desktopEnabled: boolean;
	showDisclaimer: boolean;
	shareVisualVariants?: {
		variantWithImage: string;
		variantWithoutImage: string;
	};
}>;

export const POST_CONFIGS: Readonly<Record<FullPostType, PostConfigRow>> = {
	facebook_post: {
		platform: 'facebook',
		postType: 'post',
		requiresRecommendedGroup: false,
		requiresShareVisual: false,
		imageStrategy: 'readyToSharePost',
		includeMessage: true,
		showDisclaimer: true,
		desktopEnabled: true,
		shareVisualVariants: {
			variantWithImage: 'horizontal_with_image',
			variantWithoutImage: 'horizontal_without_image',
		},
	},
	facebook_group_post: {
		platform: 'facebook',
		postType: 'post',
		requiresRecommendedGroup: true,
		requiresShareVisual: false,
		imageStrategy: 'petitionFallback',
		includeMessage: true,
		requiresGroupSelection: true,
		showDisclaimer: true,
		desktopEnabled: true,
	},
	facebook_story: {
		platform: 'facebook',
		postType: 'story',
		requiresRecommendedGroup: false,
		requiresShareVisual: true,
		imageStrategy: 'shareVisual',
		includeMessage: false,
		showDisclaimer: true,
		desktopEnabled: false,
		shareVisualVariants: {
			variantWithImage: 'story_variant_a',
			variantWithoutImage: 'story_variant_e',
		},
	},
	instagram_post: {
		platform: 'instagram',
		postType: 'post',
		requiresRecommendedGroup: false,
		requiresShareVisual: true,
		imageStrategy: 'shareVisual',
		includeMessage: true,
		showDisclaimer: true,
		desktopEnabled: false,
		shareVisualVariants: {
			variantWithImage: 'post_variant_c',
			variantWithoutImage: 'post_variant_e',
		},
	},
	instagram_story: {
		platform: 'instagram',
		postType: 'story',
		requiresRecommendedGroup: false,
		requiresShareVisual: true,
		imageStrategy: 'shareVisual',
		includeMessage: false,
		showDisclaimer: true,
		desktopEnabled: false,
		shareVisualVariants: {
			variantWithImage: 'story_variant_a',
			variantWithoutImage: 'story_variant_e',
		},
	},
	reddit_post: {
		platform: 'reddit',
		postType: 'post',
		requiresRecommendedGroup: true,
		requiresShareVisual: false,
		requiresGroupSelection: true,
		imageStrategy: 'petitionFallback',
		includeMessage: true,
		showDisclaimer: true,
		desktopEnabled: true,
	},
	reddit_group_post: {
		platform: 'reddit',
		postType: 'post',
		requiresRecommendedGroup: true,
		requiresShareVisual: false,
		imageStrategy: 'petitionFallback',
		includeMessage: true,
		requiresGroupSelection: true,
		showDisclaimer: true,
		desktopEnabled: true,
	},
};

function extractMessage(message: string | null): string {
	if (!message) return '';

	try {
		const parsed = JSON.parse(message) as MessageBody;
		if ('body' in parsed) {
			return parsed.body || '';
		}
	} catch {
		// Not JSON, use message as-is
	}

	return message;
}

function removeTrailingLink(message: string): string {
	if (!message) return '';

	// Remove HTML anchor tags at the end (e.g., <a href="...">text</a>)
	const htmlLinkPattern = /<a\s+[^>]*>.*?<\/a>\s*$/i;
	let cleaned = message.replace(htmlLinkPattern, '').trim();

	// Remove trailing URLs (http, https, www., etc.)
	const urlPattern = /\s*(?:https?:\/\/|www\.)[^\s]+$/i;
	cleaned = cleaned.replace(urlPattern, '').trim();

	return cleaned;
}

export type MappedSharingResource = {
	message: string;
	shareUrl: string;
	previewImageUrl: string;
	recommendedGroup: RecommendedGroup | undefined;
	petitionTitle: string;
};

export function mapToSharingModalProps(
	petition: StarterDashboardV2PageData['petition'],
	options: {
		fullPostType: FullPostType;
		recommendedGroup?: RecommendedGroup;
		trackingProps: SharingModalProps['trackingProps'];
	},
): SharingProps {
	const { fullPostType, trackingProps } = options;
	const row = POST_CONFIGS[fullPostType];

	const base = {
		platform: row.platform,
		postType: row.postType,
		petition,
		trackingProps,
	};

	if (row.requiresRecommendedGroup) {
		return {
			...base,
			recommendedGroup: options.recommendedGroup,
		} as FacebookPost | RedditPost; // only those types support recommendedGroup
	}

	return base;
}

export function extractSharingResource(
	fullPostType: FullPostType,
	options: {
		post?: ReadyToSharePostWithPlatform;
		petition: StarterDashboardV2PageData['petition'];
		recommendedGroup?: RecommendedGroup;
	},
): MappedSharingResource {
	const { post, petition, recommendedGroup } = options;

	const config = POST_CONFIGS[fullPostType];

	const previewImageUrl = resolvePreviewImageUrl(config.imageStrategy, {
		post,
		petition,
		postType: config.postType,
		postConfig: config,
	});

	let message = config.includeMessage && post ? extractMessage(post.message) : '';

	// TODO: Remove this link removal logic once the backend stops including links at the end of Reddit post messages
	if (config.platform === 'reddit' && message) {
		message = removeTrailingLink(message);
	}

	return {
		message,
		previewImageUrl,
		shareUrl: `https://www.change.org/p/${petition.slug}`, // TODO: Implement this to add the correct tracking params
		recommendedGroup,
		petitionTitle: petition.ask,
	};
}

function resolvePreviewImageUrl(
	imageStrategy: ImageStrategy,
	args: {
		post?: ReadyToSharePostWithPlatform;
		petition: StarterDashboardV2PageData['petition'];
		postType: SharingModalProps['postType'];
		postConfig: PostConfigRow;
	},
): string {
	const { post, petition, postType, postConfig } = args;

	const shareVisualsResolved = post?.images || petition.shareVisuals;

	switch (imageStrategy) {
		case 'shareVisual':
			return shareVisualForPost(shareVisualsResolved, postConfig)?.url || '';
		case 'readyToSharePost':
			return post ? getImageUrl(post, petition, postType) : '';
		case 'petitionFallback':
			return petition.photo?.petitionLarge.url || '';
		default:
			return '';
	}
}

function shareVisualForPost(shareVisuals: readonly ShareVisual[] | undefined, postConfig: PostConfigRow) {
	const { shareVisualVariants } = postConfig;
	if (!shareVisualVariants || !shareVisuals) return undefined;

	const { variantWithImage, variantWithoutImage } = shareVisualVariants;

	// filter the search visuals that contain either variantWithImage or variantWithoutImage
	// and are not processing. Then, sort them alphabetically and return the first one (variantWithImage will be first)
	const filteredVisuals = shareVisuals.filter(
		(visual: ShareVisual) =>
			(visual.variationName === variantWithImage || visual.variationName === variantWithoutImage) && !visual.processing,
	);
	const sortedVisuals = filteredVisuals.sort((a, b) => (a.variationName >= b.variationName ? 1 : -1));
	return sortedVisuals.at(0);
}

function getImageUrl(
	post: ReadyToSharePostWithPlatform,
	petition: StarterDashboardV2PageData['petition'],
	preferredImageType: SharingModalProps['postType'],
): string {
	// First try to find image matching the preferred type and not processing
	const matchingImage = post.images.find(
		(img) => img.imageType.toLowerCase() === preferredImageType.toLowerCase() && !img.processing,
	);

	if (matchingImage) return matchingImage.url;

	// Fallback: use first non-processing image
	const anyImage = post.images.find((img) => !img.processing);
	if (anyImage) return anyImage.url;

	// Final fallback: petition share visuals or empty string
	return petition.shareVisuals[0]?.url || '';
}
